import React, { useState } from "react";
import * as Modal from "@radix-ui/react-dialog";
import { MoreHorizontal, X, Languages, HelpCircle, Undo2, Smartphone, ChevronRight } from "lucide-react";
import { DisplayPanel } from "./DisplayMenu";
import { WorldClock } from "./WorldClock";
import { isStandalone, openInstallPrompt } from "../lib/pwa";

const Tile = ({ testid, icon: Icon, label, sub, onClick, disabled }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-3 text-xs font-semibold transition-colors ${disabled ? "border-slate-100 text-slate-300" : "border-slate-200 text-slate-700 hover:border-brand-200 hover:bg-brand-50/60 active:bg-brand-50"}`}>
    <Icon className="h-5 w-5" />
    <span>{label}</span>
    {sub && <span className="text-[10px] font-normal text-slate-400 -mt-1">{sub}</span>}
  </button>
);

/** 手机设置面板使用模态管理滚动锁和焦点，避免在面板内滑动时背后的项目列表跟着滚动。 */
export function MobileMoreMenu({ zh, lang = "zh", open: controlledOpen, onOpenChange, hideTrigger = false, onToggleLang, onTour, onUndo, canUndo, onOpenHistory, display }) {
  const nextLang = { zh: "English", en: "Español", es: "中文" }[lang] || "中文";
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = onOpenChange || setLocalOpen;
  const close = () => setOpen(false);
  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      {!hideTrigger && <Modal.Trigger asChild><button type="button" data-testid="topbar-more" aria-label={zh ? "设置与工具" : "Settings & tools"}
        className="md:hidden h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors">
        <MoreHorizontal className="h-5 w-5" />
      </button></Modal.Trigger>}
      <Modal.Portal>
          <Modal.Overlay className="fixed inset-0 z-[10050] bg-slate-900/45 backdrop-blur-[2px]" data-testid="mobile-more-backdrop" />
          <Modal.Content aria-describedby={undefined} data-testid="mobile-more-sheet"
            onCloseAutoFocus={(event) => {
              if (hideTrigger) {
                event.preventDefault();
                // 平板无底部导航，桌面折叠侧栏也能打开设置；回到当前断点仍可见的入口。
                const trigger = ["mobile-bottom-more", "mobile-tools-open", "mobile-nav-open"].map(id => document.querySelector(`[data-testid="${id}"]`)).find(el => el?.getClientRects().length);
                trigger?.focus();
              }
            }}
            className="fixed inset-x-0 bottom-0 z-[10051] rounded-t-3xl bg-white shadow-2xl max-h-[calc(100dvh-env(safe-area-inset-top)-1rem)] overflow-y-auto overscroll-contain safe-bottom-pad">
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-4 pt-4 pb-2 flex items-center justify-between border-b border-slate-100">
              <span className="absolute left-1/2 -translate-x-1/2 top-1.5 h-1 w-10 rounded-full bg-slate-200" />
              <Modal.Title className="text-base font-bold text-slate-800">{zh ? "设置与工具" : "Settings & tools"}</Modal.Title>
              <Modal.Close aria-label={zh ? "关闭设置" : "Close settings"} data-testid="mobile-more-close" className="h-11 w-11 -mr-1 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></Modal.Close>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <Tile testid="more-lang" icon={Languages} label={nextLang} sub={zh ? "切换语言" : lang === "es" ? "Idioma" : "Language"} onClick={onToggleLang} />
                <Tile testid="more-tour" icon={HelpCircle} label={zh ? "新手引导" : "Tour"} onClick={() => { close(); onTour(); }} />
                <Tile testid="more-undo" icon={Undo2} label={zh ? "撤销" : "Undo"} onClick={() => { close(); (onOpenHistory || onUndo)(); }} />
              </div>
              {!isStandalone() && (
                <button type="button" data-testid="more-install" onClick={() => { close(); openInstallPrompt(); }}
                  className="w-full flex items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/60 px-3 py-3 text-left">
                  <span className="h-9 w-9 rounded-xl bg-brand-grad text-white flex items-center justify-center shadow-brand"><Smartphone className="h-4 w-4" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-slate-800">{zh ? "安装到手机主屏幕" : "Add to home screen"}</span>
                    <span className="block text-[11px] text-slate-500">{zh ? "全屏打开，像 App 一样使用" : "Open full-screen like a native app"}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              )}
              <div className="rounded-2xl border border-slate-100 p-3">
                <DisplayPanel zh={zh} {...display} />
              </div>
              <WorldClock embedded />
            </div>
          </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
}

export default MobileMoreMenu;
